import {
  keyBy,
  sortBy,
  map,
  reduce,
  pickBy,
  toString,
  get,
  filter,
} from 'lodash';
import { createSelector, createStructuredSelector } from 'reselect';
import { getPermissions, getCurrentUserData } from '../../utils/selectors';
import { dateToFormat } from '../../utils/DateTime';
import { isCreateCrewRotationButtonVisible } from '../crew-list/selector';
import { getNoUserNameTextByStatus } from '../../utils/timeplans';
import { slotProblems } from './slotProblems';

const MONTHS_MS_THRESHOLD = 30 * 24 * 60 * 60 * 1000; // 30 days in ms

const didLoad = (s) => s.timeplans.didLoad;
const state = (s) => s.timeplans.state;
const getFilter = (s) => s.timeplans.filter;
const getAppliedFilter = (s) => s.timeplans.appliedFilter;
const crewListMode = (s) => s.crewList.mode;

export const getAssignedVesselsData = (s) => s.assignedVessels.data;
export const getVesselOwnersData = (s) => s.vesselOwners.data;
const getTimeplans = (s) => s.timeplans.data;

const getManningScaleById = createSelector(
  [getAssignedVesselsData],
  ({ manningScales }) => keyBy(manningScales, 'originId'),
);

const applyAdditionalFilter = (filterData, manningScales) => {
  const additionalFilter = get(filterData, 'additionalFilter', 'none');
  if (additionalFilter === 'none') {
    return manningScales;
  }
  const filteredManningScales = filter(manningScales, additionalFilter);
  return filteredManningScales;
};

const getManningScalesData = createSelector(
  [getAssignedVesselsData, getAppliedFilter],
  ({ manningScales }, stateFilter) => {
    const withAdditionalFilter = applyAdditionalFilter(stateFilter, manningScales);

    return withAdditionalFilter.reduce(
      (acc, ms) =>
        stateFilter[`roles.${ms.role_id}`] &&
        stateFilter[`vessels.${ms.vessel_id}`]
          ? {
            manningScales: [...acc.manningScales, ms],
            msCountByVesselId: {
              ...acc.msCountByVesselId,
              [ms.vessel_id]: (acc.msCountByVesselId[ms.vessel_id] || 0) + 1,
            },
          }
          : acc,
      {
        manningScales: [],
        msCountByVesselId: {}
      }
    );
  }
);

// CREW-1245 — these rows and the label strip built by `getVessels` below are
// two halves of one picture: each label is drawn `rolesCount` rows tall and
// slices this list in order. So the two MUST order vessels the same way.
//
// They did not, and it showed on 11.09.2026: `_AD_5S50ME-B` (MASTER + CHIEF
// ENGINEER) and `_AD_6RTA48T-B` (MASTER) came out as "MASTER, MASTER" under the
// first label and "CHIEF ENGINEER" under the second. Grouping with
// `Object.keys` returns numeric-like ids ASCENDING BY ID, which is not the
// alphabetical order the labels now use — and every vessel after the first was
// mislabelled, on every owner's board.
//
// It looked fine before only because the old label sort compared `NaN` to `NaN`
// (`created_at` is not in the board's fieldset) and so reordered nothing.
const getResources = createSelector(
  [getManningScalesData],
  ({ manningScales }) => {
    const groupedByVessel = manningScales.reduce((acc, ms) => {
      if (!acc[ms.vessel_id]) {
        acc[ms.vessel_id] = [];
      }
      acc[ms.vessel_id].push(ms);
      return acc;
    }, {});

    return Object.values(groupedByVessel)
      .map((group) => group.sort((a, b) => a.crewing_priority - b.crewing_priority))
      .sort((a, b) => compareVesselNames(a[0]?.vesselName, b[0]?.vesselName))
      .flat();
  },
);

// CREW-1245 — the board used to list vessels by when they were added, so for
// Polsteam it opened on ISADORA (added 06.02.2015) and the rest had no readable
// order at all. Alphabetical, and `localeCompare` sorts `_` ahead of every
// letter on its own — which is what makes the `_AD_…` advertising vessels sit
// at the top of the list where the recruiter is looking (the customer asked for
// the prefix on exactly that understanding). `numeric` because the advertising
// names end in engine numbers, and a plain string sort puts 10 before 2.
//
// A row can genuinely have no name: it comes from spreading
// `assignedVesselsByVesselId[vesselId]`, which is undefined whenever the
// manning-scale counts know a vessel the assigned list does not.
export const compareVesselNames = (a, b) => `${a || ''}`
  .localeCompare(`${b || ''}`, undefined, { numeric: true });

export const byVesselName = (a, b) => compareVesselNames(a?.name, b?.name);

const getVessels = createSelector(
  [getAssignedVesselsData, getManningScalesData],
  ({ assignedVesselsByVesselId }, { msCountByVesselId }) => map(msCountByVesselId, (rolesCount, vesselId) => ({
    ...assignedVesselsByVesselId[vesselId],
    rolesCount,
  }))
    .sort(byVesselName),
);

const getOptionsOfVessels = createSelector(
  [getAssignedVesselsData, getFilter],
  ({ assignedVesselsByVesselId }, filterObj) => {

    const ownerIds = Object.keys(pickBy(filterObj, (v, k) => v && k.includes('owners')))
      .map((k) => k.split('.')[1]);

    const vessels = ownerIds.includes('all')
      ? assignedVesselsByVesselId
      : pickBy(assignedVesselsByVesselId, (v) => v.owner_id && ownerIds.includes(toString(v.owner_id)));

    return map(
      vessels,
      ({
        name,
        imo,
        owner_id,
      }, vesselId) => ({
        value: filterObj[`vessels.${vesselId}`] && filterObj[`owners.${owner_id}`],
        field: `vessels.${vesselId}`,
        label: `${name} (${imo})`,
        name,
        ownerId: owner_id && toString(owner_id),
      }),
    )
      // CREW-1245 — the same complaint as the board rows above, on the same
      // screen: this is the panel you pick vessels in, and by date-added the
      // `_AD_…` ones were scattered through it. `createdAt` was carried on the
      // option only to feed that sort, and nothing else reads it, so it makes
      // way for `name`.
      .sort(byVesselName);
  },
);

const getRolesBySelectedVessels = createSelector(
  [getFilter, getAssignedVesselsData],
  (filterData, { manningScales }) => manningScales
    .filter((ms) => filterData[`vessels.${ms.vessel_id}`])
    .reduce((acc, ms) => (
        !acc[ms.role_id] ? ({
          ...acc,
          [ms.role_id]: {
            value: filterData[`roles.${ms.role_id}`],
            field: `roles.${ms.role_id}`,
            label: ms.title,
            priority: ms.crewing_priority,
          },
        }) : acc),
      {}),
);

const getOptionsOfVesselOwners = createSelector(
  [getVesselOwnersData, getFilter],
  (data, filterData) => map(
    data,
    (owner) => ({
      value: filterData[`owners.${owner.id}`],
      field: `owners.${owner.id}`,
      label: owner.attributes.title,
    }),
  ),
);

const getOptionsOfRoles = createSelector(
  [getRolesBySelectedVessels],
  (rolesById) => sortBy(rolesById, 'priority'),
);

const isValidFilter = createSelector(
  [getFilter, getRolesBySelectedVessels],
  (filter, roleById) => {
    const {
      atLeastOneVesselSelected,
      atLeastOneRoleSelected
    } = reduce(filter, (acc, isSelected, propName) => {
      if (acc.atLeastOneVesselSelected && acc.atLeastOneRoleSelected) return acc;

      const [type, id] = propName.split('.');
      const nextAcc = {};

      if (acc.atLeastOneVesselSelected || (type === 'vessels' && isSelected)) {
        nextAcc.atLeastOneVesselSelected = true;
      }

      if (acc.atLeastOneRoleSelected || (type === 'roles' && isSelected && roleById[id])) {
        nextAcc.atLeastOneRoleSelected = true;
      }

      return nextAcc;
    }, {
      atLeastOneVesselSelected: false,
      atLeastOneRoleSelected: false
    });

    return atLeastOneVesselSelected && atLeastOneRoleSelected;
  },
);

export const getEvents = createSelector(
  [getTimeplans, getAssignedVesselsData, getManningScaleById, getCurrentUserData],
  (timeplans, { assignedVesselsByVesselId }, manningScaleById, currentUser) => timeplans.reduce((acc, {
    id,
    start,
    end,
    travel_start,
    travel_end,
    travel_payment_included,
    status,
    manning_scale_id,
    crew_change_sign_off_id: offChId,
    crew_change_sign_on_id: onChId,
    userName,
    promotionsCount,
    readinessDate,
    userId,
    avatar,
    port,
    lastComment,
    offer,
    vacancy_description,
    vacancy_id,
    to_publish,
    external_only,
    repair_team,
    has_contract_template,
    document_templates,
    has_shipman_comment,
    vessel_name,
    role_name,
    telegram_bot,
    problem_newcomer,
    last_comment_body,
    vacancy_candidates,
    // CREW-1225 — whether the fleet side holds this ship. The slot form gates
    // its date and travel rules on it, so it has to survive this mapping: a key
    // the reduce does not name is a key the form never sees.
    dax_managed,
    // CREW-1295 — the slot wage's stored total, for the missing-data check.
    wages_total,
  }) => {
    // A slot with no dates cannot be drawn: DayPilot throws "Event data item
    // must specify 'start' property" from inside componentDidUpdate, which
    // React cannot recover from — the whole page goes white for everyone
    // looking at that vessel (2026-08-17, again 2026-08-28). The backend
    // refuses to store such a row now; this keeps one bad row from taking the
    // scheduler down if it ever gets in another way (import, older data,
    // direct SQL).
    if (!start || !end) return acc;

    const {
      name: role,
      originRoleId,
      originRoleName,
      role_id,
      vessel_id,
      originId,
      crewing_priority: rolePriority,
      createdAt: roleCreatedAt,
      template_offer,
    } = manningScaleById[manning_scale_id] || {};

    const isOnlyTimeplanView = currentUser.role === 'partner_view';
    let text = getNoUserNameTextByStatus(status, userName, promotionsCount);

    if (text === 'Vacancy') {
      if (to_publish && !isOnlyTimeplanView) text = `🌐 ${text}`;
      if (offer && !isOnlyTimeplanView) text = `${text} [$${offer}]`;
    } else if (text !== 'Other agency') {
      if (telegram_bot) text = `🤖 ${text}`;
    }

    if (repair_team) {
      text = `🛠️ ${text}`;
    }

    if (problem_newcomer) {
      text = `📋 ${text}`;
    }

    if (port) text = `(${dateToFormat(start, 'dd.MM')} ${port}) ${text}`;

    if (['to_propose', 'pending_approval', 'approved'].includes(status) && readinessDate) {
      text = `${text} - DOA ${dateToFormat(readinessDate, 'dd.MM')}`;
    }

    if (last_comment_body) text = `${text} - ${last_comment_body}`;

    // The candidates in play on this open position — the scheduler badges the
    // count and lists them on hover, so a planner sees who is being considered
    // without leaving /timeplans. The backend already withholds them for a
    // filled slot; partner_view sees none of the vacancy decorations, and an
    // empty list means "draw nothing".
    const applyCandidates = isOnlyTimeplanView ? [] : (vacancy_candidates || []);

    // CREW-1295 — what this slot still lacks at its stage; the board draws an
    // info button when the list is not empty. Only on ships the fleet side holds
    // (`dax_managed`, the same gate as CREW-1225): DAX is what needs this data.
    // partner_view gets none of it, like the vacancy decorations.
    const problems = isOnlyTimeplanView || !dax_managed ? [] : slotProblems({
      status, start, end, travel_start, travel_end, travel_payment_included, wages_total,
    });

    const isUrgentVacancy = status === 'vacancy' && (new Date(start) - new Date()) < MONTHS_MS_THRESHOLD;
    
    const isVacancyPage = window.location.pathname.includes('vacancies');

    if (!originId && !isVacancyPage) return acc; // temp fix for timeplans fi archived manning scales

    return [
      ...acc,
      {
        id,
        offChId,
        onChId,
        start,
        end,
        travel_start,
        travel_end,
        travel_payment_included,
        text,
        resource: `${originId}:${role_id}:${vessel_id}`,
        msId: originId,
        // next data for the form
        status,
        vesselId: vessel_id,
        vessel: get(assignedVesselsByVesselId, `${vessel_id}.name`) || vessel_name,
        roleId: role_id,
        role: role || role_name,
        originRoleId,
        originRoleName,
        rolePriority,
        roleCreatedAt,
        userId,
        avatar,
        port,
        userName,
        promotionsCount,
        readinessDate,
        offer,
        vacancy_description,
        vacancy_id,
        to_publish,
        external_only,
        repair_team,
        template_offer,
        has_contract_template,
        document_templates,
        applyCandidates,
        dax_managed,
        wages_total,
        problems,
        cssClass: [
          'timeplan',
          `timeplan__${status}${isUrgentVacancy ? "__urgent" : ""}`,
          `timeplan__${id}`,
          offChId ? 'timeplan__has_off' : '',
          onChId ? 'timeplan__has_on' : '',
          has_shipman_comment ? 'timeplan__shipman' : '',
          problems.length > 0 ? 'timeplan__has_problems' : '',
        ].join(' '),
      },
    ];
  }, []),
);

export default createStructuredSelector({
  didLoad,
  state,
  filter: getFilter,
  resources: getResources,
  vessels: getVessels,
  assignedVessels: getAssignedVesselsData,
  optionsOfVessels: getOptionsOfVessels,
  optionsOfRoles: getOptionsOfRoles,
  optionsOfOwners: getOptionsOfVesselOwners,
  events: getEvents,
  permissions: getPermissions,
  isValidFilter,
  crewListMode,
  isCreateCrewRotationButtonVisible,
  currentUser: getCurrentUserData,
});
